import { ConnectionQuality } from 'livekit-client';
import { SignalHigh, SignalLow, SignalMedium, SignalZero, type LucideIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const QUALITY: Record<
  ConnectionQuality,
  { label: string; short: string; icon: LucideIcon; tone: string; className: string }
> = {
  [ConnectionQuality.Excellent]: {
    label: 'Good connection',
    short: 'Good',
    icon: SignalHigh,
    tone: 'good',
    className: 'text-[var(--success)]',
  },
  [ConnectionQuality.Good]: {
    label: 'Good connection',
    short: 'Good',
    icon: SignalMedium,
    tone: 'good',
    className: 'text-[var(--success)]',
  },
  [ConnectionQuality.Poor]: {
    label: 'Weak connection',
    short: 'Weak',
    icon: SignalLow,
    tone: 'warn',
    className: 'text-[var(--warning)]',
  },
  [ConnectionQuality.Lost]: {
    label: 'Connection lost',
    short: 'Lost',
    icon: SignalZero,
    tone: 'bad',
    className: 'text-[color-mix(in_oklab,var(--destructive)_80%,white)]',
  },
  [ConnectionQuality.Unknown]: {
    label: 'Checking connection',
    short: 'Checking',
    icon: SignalMedium,
    tone: 'idle',
    className: 'text-muted-foreground',
  },
};

/**
 * Ambient connection readout. Compact by design: it sits in the call header
 * next to the meeting title, and a wall of text there competes with the video.
 */
export function NetworkStatus({
  quality,
  videoPaused,
}: {
  quality: ConnectionQuality;
  videoPaused: boolean;
}) {
  const { label, short, icon: Icon, tone, className } = QUALITY[quality];

  return (
    <div className="flex items-center gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'network-pill border-border/70 inline-flex items-center gap-1.5 rounded-full border bg-white/[0.04] px-2.5 py-1 text-xs font-medium',
              `is-${tone}`,
              className,
            )}
          >
            <Icon className="size-3.5" strokeWidth={2.5} />
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden">{short}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>

      {videoPaused && (
        <span
          role="status"
          className="network-note border-[color-mix(in_oklab,var(--warning)_35%,transparent)] bg-[color-mix(in_oklab,var(--warning)_12%,transparent)] hidden rounded-full border px-2.5 py-1 text-xs text-[var(--warning)] lg:inline"
        >
          Video paused to protect your audio
        </span>
      )}
    </div>
  );
}
